import { useCallback, useRef, useState } from 'react';

import { useTranslation } from '@/i18n';

import { monthlyCutsService } from '@/api/domains/attendance';
import { useExport } from '@/hooks/ui/useExport';


export interface UseExportPaymentConceptsResult {
  exportConceptos: (monthlyCutId: number) => Promise<void>;
  /** Ids de los cortes cuya descarga está en vuelo. Permite deshabilitar la acción por fila. */
  exportingCutIds: ReadonlySet<number>;
}

export function useExportPaymentConcepts(): UseExportPaymentConceptsResult {
  const { t: translate } = useTranslation();

  const { onExport } = useExport<number>({
    exportFn: (monthlyCutId) => monthlyCutsService.exportConceptsFile(monthlyCutId),
    successMessage: translate('attendance:monthlyCutsList.conceptsExport.messages.exportSuccess'),
    errorMessage: translate('attendance:monthlyCutsList.conceptsExport.messages.exportError'),
  });

  // `useExport` no trae guard de concurrencia: un doble click dispararía dos descargas. Se rastrea
  // por corte (no un booleano global) para que dos cortes distintos sí puedan bajarse en paralelo.
  const [exportingCutIds, setExportingCutIds] = useState<ReadonlySet<number>>(new Set());
  // Espejo sincrónico del set: el guard debe rechazar el segundo click antes de que React
  // re-renderice, cosa que el estado no garantiza.
  const inFlightRef = useRef<Set<number>>(new Set());

  const exportConceptos = useCallback(async (monthlyCutId: number) => {
    if (inFlightRef.current.has(monthlyCutId)) return;

    inFlightRef.current.add(monthlyCutId);
    setExportingCutIds(new Set(inFlightRef.current));

    try {
      await onExport(monthlyCutId);
    } finally {
      inFlightRef.current.delete(monthlyCutId);
      setExportingCutIds(new Set(inFlightRef.current));
    }
  }, [onExport]);

  return { exportConceptos, exportingCutIds };
}
